"use client";

import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import type { INewsFilterData } from "../../interfaces/NewsFilter.interface";
import type { INewsItem } from "../../interfaces/News.interface";
import type { NewsAEMContent } from "../../interfaces/NewsAEM.interface";
import NewsFilter from "./NewsFilter";
import WatchlistNews from "./tabs/WatchlistNews";
import PortfolioNews from "./tabs/PortfolioNews";
import Trending from "./sections/Trending";
import OtherNews from "./sections/OtherNews";
import { trackComponent } from "@repo/analytics";
import {
  componentNameConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import Verify2FA from "./components/Verify2FA";
import { handleRedirection } from "../../utils/helper";
import {
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  useAppSelector,
} from "@repo/web-store";
import { getNewsData } from "../../services/TradingMallService";
import { buildFilterArray } from "./utils/filterUtils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import { newsStore } from "./store/newsStore";
import {
  campaignName,
  journeyTag,
  productCategory,
  propertyType,
  referenceId,
  sprint,
  subSection,
} from "../../pages/News/newsTrackingConstants";
import { getSessionData } from "@repo/shared-utils/common-utils";

interface StockNewsClientProps {
  filterOptions?: INewsFilterData | null;
  aemContent?: NewsAEMContent | null;
  initialTrendingNews: INewsItem[];
  initialOtherNews: INewsItem[];
  initialWatchlistNews: INewsItem[];
  initialPortfolioNews: INewsItem[];
}

/**
 * Client component that renders all tabs for SEO
 * All tabs are rendered (hidden/shown) so content is in view source
 */
const StockNewsClient: React.FC<StockNewsClientProps> = ({
  filterOptions,
  aemContent,
  initialTrendingNews,
  initialOtherNews,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialWatchlistNews,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialPortfolioNews,
}) => {
  const leadStatus = useAppSelector(selectLeadStatus);
  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );

  const [authcode, setAuthcode] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("latest");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<string[]>([]);
  const [selectedInstrument, setSelectedInstrument] = useState<string[]>([]);
  const [selectedMarketCap, setSelectedMarketCap] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<string[]>([]);
  const filtersRestoredRef = useRef(false);

  // Restore filters from sessionStorage on mount
  useEffect(() => {
    if (filtersRestoredRef.current) return;

    const restoredFilters = newsStore.getFilterSelections();
    if (restoredFilters) {
      setSelectedEvents(restoredFilters.events);
      setSelectedIndex(restoredFilters.index);
      setSelectedInstrument(restoredFilters.instrument);
      setSelectedMarketCap(restoredFilters.marketCap);
      setSelectedSector(restoredFilters.sector);
    }
    filtersRestoredRef.current = true;
  }, []);

  // Use AEM data for tabs, fallback to defaults
  const tabs = [
    {
      label: aemContent?.tabs?.latest ?? "Latest",
      value: "latest",
    },
    {
      label: aemContent?.tabs?.watchlist ?? "Watchlist",
      value: "watchlist",
    },
    {
      label: aemContent?.tabs?.portfolio ?? "Portfolio",
      value: "portfolio",
    },
  ];

  const handleTabChange = (newTab: string) => {
    if (activeTab === newTab) return;

    setActiveTab(newTab);
    const tabIndex = tabs.findIndex((tab) => tab.value === newTab);
    const selectedTab = tabs[tabIndex];
    const ctaText = selectedTab?.label ?? newTab;
    const tabPosition = (tabIndex + 1).toString();

    (async () => {
      const customerId = await getCustomerIdForAnalytics();

      await trackComponent({
        componentName: componentNameConstants.TABS,
        sectionTitle: "navigation_tabs_header_selection_secondary",
        eventType: dlEventConstants.TABS_TAB_CLICK,
        ctaText,
        productCategory,
        subSection,
        cleverTapOverrides: {
          EP_JOURNEY: journeyTag,
          EP_PROPERTY_TYPE: propertyType,
          EP_PRODUCT_NAME: subSection,
          EP_CAMPAIGN: campaignName,
          EP_SPRINT: sprint,
          EP_REFERENCE_ID: referenceId,
          ...(customerId && { EP_CUSTOMER_ID: customerId }),
          EP_SR_NUMBER: tabPosition,
          EP_ENTRYPOINT: "navigation_tabs_header_selection_secondary",
          EP_TAB: newTab.toUpperCase(),
        },
      });
    })();
  };

  const clearAll = () => {
    setSelectedEvents([]);
    setSelectedIndex([]);
    setSelectedInstrument([]);
    setSelectedMarketCap([]);
    setSelectedSector([]);
    newsStore.clearFilterSelections();
  };

  const filters = useMemo(
    () => ({
      events: selectedEvents,
      index: selectedIndex,
      instrument: selectedInstrument,
      marketCap: selectedMarketCap,
      sector: selectedSector,
    }),
    [selectedEvents, selectedIndex, selectedInstrument, selectedMarketCap, selectedSector]
  );

  // Wrapper functions for filter setters
  const handleEventsChange = useCallback((events: string[]) => {
    setSelectedEvents(events);
  }, []);

  const handleIndexChange = useCallback((index: string[]) => {
    setSelectedIndex(index);
  }, []);

  const handleInstrumentChange = useCallback((instrument: string[]) => {
    setSelectedInstrument(instrument);
  }, []);

  const handleMarketCapChange = useCallback((marketCap: string[]) => {
    setSelectedMarketCap(marketCap);
  }, []);

  const handleSectorChange = useCallback((sector: string[]) => {
    setSelectedSector(sector);
  }, []);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    newsStore.saveFilterSelections(filters);
  }, [filters]);

  // State for client-side fetched news (null means use SSR data)
  const [clientTrendingNews, setClientTrendingNews] = useState<
    INewsItem[] | null
  >(null);
  const [trendingError, setTrendingError] = useState(false);
  const filterArray = useMemo(() => buildFilterArray(filters), [filters]);
  // Fetch trending news when filters change (only when filters are active)
  // Note: OtherNews handles its own data fetching via useOtherNews hook
  useEffect(() => {
    // If no filters, reset to SSR data
    if (!filterArray || filterArray.length === 0) {
      setClientTrendingNews(null);
      setTrendingError(false);
      return;
    }
    const fetchNews = async () => {
      try {
        const trendingResponse = await getNewsData({
          newsType: "trending",
          filters: filterArray,
          page: 1,
          limit: 10,
        }).catch(() => {
          setTrendingError(true);
          return [] as INewsItem[];
        });

        if (Array.isArray(trendingResponse)) {
          setClientTrendingNews(trendingResponse);
          setTrendingError(false);
        }
      } catch (error) {
        console.error("Error fetching news:", error);
      }
    };

    fetchNews();
  }, [filterArray]);

  // Use client data if available (filters applied), otherwise use SSR data
  const trendingNews = clientTrendingNews ?? initialTrendingNews;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const code = window.location.search
        ? new URLSearchParams(window.location.search).get("code")
        : null;
      setAuthcode(code);
      if (code) {
        setActiveTab("portfolio");
      }
    }
  }, []);

  const handleAuthenticate = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const ctaText = aemContent?.authentication?.buttonText ?? "Authenticate";
      const sectionTitle =
        aemContent?.authentication?.message ?? "Authenticate";

      // Fire-and-forget CT tracking so navigation is not delayed.
      void (async () => {
        try {
          const customerId = await getCustomerIdForAnalytics();

          await trackComponent({
            componentName: "master_cta_button_floating",
            sectionTitle,
            eventType: "master_cta_button_floating_button_click",
            ctaText,
            productCategory,
            subSection,
            cleverTapOverrides: {
              EP_JOURNEY: journeyTag,
              EP_PROPERTY_TYPE: propertyType,
              EP_PRODUCT_NAME: subSection,
              EP_CAMPAIGN: campaignName,
              EP_SPRINT: sprint,
              EP_REFERENCE_ID: referenceId,
              ...(customerId && { EP_CUSTOMER_ID: customerId }),
              EP_SR_NUMBER: "1",
              EP_CUST_ATTRIBUTE_1: "floating_cta",
            },
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            "Failed to track master_cta_button_floating event",
            error,
          );
        }
      })();

      let redirectUrl = isOpenInApp()
        ? aemContent?.urls?.loggedInMFAUrlApp
        : aemContent?.urls?.loggedInMFAUrlWeb;
      if (redirectUrl) {
        if (redirectUrl.includes("<<mobileNumber>>")) {
          try {
            const { mobileNo } = await getSessionData();
            redirectUrl = redirectUrl.replace(/<<mobileNumber>>\s*/g, mobileNo.trim());
          } catch (error) {
            console.error("Error fetching session data:", error);
          }
        }
        handleRedirection(redirectUrl);
      }
    },
    [
      aemContent?.authentication?.buttonText,
      aemContent?.authentication?.message,
      aemContent?.urls,
      productCategory,
      subSection,
      journeyTag,
      propertyType,
      campaignName,
      sprint,
      referenceId,
    ],
  );

  return (
    <div className="bg-white">
      {isAuthSuccessOrPerpetual && (
        <div className="px-4 pt-4">
          <BFLTabs
            tabs={leadStatus === "ETB" ? tabs : tabs?.slice(0, 2)}
            selectedTab={activeTab}
            onTabChange={handleTabChange}
            color="text-[#002953]"
            className="overflow-hidden border-0"
            gapClassName="gap-8 max-sm:h-[35px]"
            tabClassName="text-base font-medium leading-none pb-4 max-sm:pb-[8px]"
            indicatorClassName="bg-[#ff6b35] h-0.5 md:h-0.5 md:bottom-0"
          />
        </div>
      )}
      <div className="relative">
        {activeTab === "portfolio" && !authcode ? (
          <Verify2FA
            buttonText={
              aemContent?.authentication?.buttonText ?? "Authenticate"
            }
            text={
              aemContent?.authentication?.message ??
              "Please authenticate to view portfolio news."
            }
            onClick={handleAuthenticate}
            className={aemContent?.portfolio?.customClass ?? ""}
          />
        ) : null}

        <div className="h-px w-full bg-gray-200"></div>

        <NewsFilter
          filterOptions={filterOptions ?? null}
          selectedEvents={selectedEvents}
          selectedIndex={selectedIndex}
          selectedInstrument={selectedInstrument}
          selectedMarketCap={selectedMarketCap}
          selectedSector={selectedSector}
          onEventsChange={handleEventsChange}
          onIndexChange={handleIndexChange}
          onInstrumentChange={handleInstrumentChange}
          onMarketCapChange={handleMarketCapChange}
          onSectorChange={handleSectorChange}
          onClearAll={clearAll}
          aemContent={aemContent}
        />

        {/* Render all tabs for SEO - all content in view source */}
        {/* Latest Tab Content - visible in HTML for SEO */}
        <div
          className={activeTab === "latest" ? "" : "hidden"}
          data-tab="latest"
        >
          <Trending
            trendingNews={trendingNews}
            aemContent={{
              ...aemContent?.trending,
              images: {
                ...aemContent?.images,
                ...aemContent?.trending?.images,
              },
              timestampLabels: {
                ...aemContent?.timestampLabels,
                ...aemContent?.trending?.timestampLabels,
              },
            }}
            hasError={trendingError}
            filters={filters}
            componentPosition={aemContent?.componentPosition}
          />
          <div className="h-px w-full bg-gray-200"></div>
          <OtherNews
            initialNews={initialOtherNews}
            aemContent={{
              ...aemContent?.otherNews,
              images: {
                ...aemContent?.images,
                ...aemContent?.otherNews?.images,
              },
              timestampLabels: {
                ...aemContent?.timestampLabels,
                ...aemContent?.otherNews?.timestampLabels,
              },
            }}
            filters={filterArray}
            newsFilters={filters}
            componentPosition={aemContent?.componentPosition}
          />
        </div>

        {/* Watchlist Tab Content - conditionally rendered */}
        {activeTab === "watchlist" && (
          <div data-tab="watchlist">
            <WatchlistNews
              filters={filters}
              aemContent={aemContent as NewsAEMContent}
            />
          </div>
        )}

        {/* Portfolio Tab Content - conditionally rendered */}
        {activeTab === "portfolio" &&
          (authcode ? (
            <div data-tab="portfolio">
              <PortfolioNews
                filters={filters}
                authcode={authcode}
                aemContent={aemContent as NewsAEMContent}
              />
            </div>
          ) : (
            <div className="h-[624px] max-sm:h-[500px]" />
          ))}
      </div>
    </div>
  );
};

export default StockNewsClient;